import { TaskDefinition } from '@/feature/execution-board'

import { useSingleVehicle } from '@/lib/features/Assets/api'

import { useObservatoryAssets } from '../../hooks/useObservatoryAssets'
import { usePersistentObservatoryPageStore } from '../../store/usePersistentObservatoryPageStore'
import { AssetMarker } from '../markers/AssetMarker/AssetMarker'

interface TaskAssignedAssetMarkerProps {
  task: TaskDefinition
}

/**
 * Renders the live marker for the focused task's assigned vehicle. Used while a task is focused —
 * the general asset layer is hidden then, so this keeps just the one relevant vehicle visible.
 *
 * The task only carries the vehicle id, so resolve it to the vehicle's `vehicleBridgeId` (the key
 * the asset list and live telemetry share) and render the matching org asset with the standard
 * AssetMarker, so it looks and behaves like any other vehicle on the map.
 *
 * This reads the same (search-cleared on focus) asset list as the rest of the map — see
 * ObservatoryPage, which clears the Assets aside search when a task is focused so the assigned
 * vehicle isn't filtered out from view.
 */
export const TaskAssignedAssetMarker = ({ task }: TaskAssignedAssetMarkerProps) => {
  const setSelectedItem = usePersistentObservatoryPageStore((state) => state.setSelectedItem)

  const { data: vehicle } = useSingleVehicle(
    task.assignedVehicle ? { id: task.assignedVehicle.id } : null,
  )
  const assets = useObservatoryAssets()

  const asset = vehicle?.vehicleBridgeId
    ? assets.data?.results?.find((candidate) => candidate.vehicleBridgeId === vehicle.vehicleBridgeId)
    : undefined

  if (!asset) {
    return null
  }

  return <AssetMarker asset={asset} onClick={() => setSelectedItem({ id: asset.id, type: 'asset' })} />
}
